import * as React from 'react';

import * as Sentry from '@sentry/react';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { get as getUser } from 'js/lib/user';

import { getCoachItemType as getCoachItemTypeName, pluginIsACoachItem } from 'bundles/coach-item/common/lib';
import type { CoachItemType } from 'bundles/coach-item/common/types';
import type { Item } from 'bundles/learner-progress/types/Item';
import { useNaptime } from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import OnDemandWidgetSessionsV1 from 'bundles/naptimejs/resources/onDemandWidgetSessionsV1';

const coachItemTypeCache = new Map<string, CoachItemType | undefined>();

// Add reset function for tests
export const resetCoachItemTypeCache = () => {
  coachItemTypeCache.clear();
};

const getCoachItemType = async (naptime: InjectedNaptime, id: string) => {
  if (coachItemTypeCache.has(id)) {
    return coachItemTypeCache.get(id);
  }

  try {
    const naptimeResponse = await naptime.executeMutation(
      OnDemandWidgetSessionsV1.action(
        'getIframeTitle',
        {},
        {
          id,
        }
      )
    );

    const { data } = naptimeResponse;

    const isPluginCoachItem = pluginIsACoachItem(data);

    const coachItemTypeName = isPluginCoachItem ? getCoachItemTypeName(data) : undefined;

    coachItemTypeCache.set(id, coachItemTypeName);

    return coachItemTypeName;
  } catch (error) {
    Sentry.captureException(error);
    coachItemTypeCache.set(id, undefined);
    return undefined;
  }
};

const useCoachItemType = (computedItem: Item, skip?: boolean): CoachItemType | undefined => {
  const { naptime } = useNaptime(() => ({}));

  const { courseId, id: itemId } = computedItem;
  const userId = getUser().id;
  const cacheKey = tupleToStringKey([userId.toString(), courseId, itemId]);

  // @ts-expect-error ts-migrate(7009) FIXME: update ContentSummary type to include the 'ungradedWidget' typeName
  const isUngradedPlugin = computedItem.contentSummary.typeName === 'ungradedWidget';

  const coachItemType = React.useMemo(() => {
    if (!isUngradedPlugin || skip) {
      return undefined;
    }
    if (coachItemTypeCache.has(cacheKey)) {
      return coachItemTypeCache.get(cacheKey);
    }

    getCoachItemType(naptime, cacheKey);

    return undefined;
  }, [naptime, cacheKey, isUngradedPlugin, skip]);

  return coachItemType;
};

export default useCoachItemType;
